import React, { useState } from "react";
import {
  TREATMENT_RECOVERY_ACTION,
  TREATMENT_RECOVERY_ACTION_LABEL,
} from "./treatmentApiConstants";
import {
  TREATMENT_RECOVERY_COPY,
  TREATMENT_RECOVERY_STATE,
} from "./treatmentUiConstants";
import "./treatmentCheckoutRecovery.css";

const treatmentName = (treatment) =>
  treatment.program_name ||
  treatment.treatment_type_name ||
  treatment.program_snapshot?.name ||
  "Treatment";

const recoveryCopy = (recovery) => {
  switch (recovery?.stage) {
    case TREATMENT_RECOVERY_STATE.authorizationFailed:
      return TREATMENT_RECOVERY_COPY.authorizationFailed;
    case TREATMENT_RECOVERY_STATE.authorizationPending:
      return TREATMENT_RECOVERY_COPY.authorizationPending;
    case TREATMENT_RECOVERY_STATE.authorized:
      return TREATMENT_RECOVERY_COPY.authorized;
    case TREATMENT_RECOVERY_STATE.providerStarted:
      return TREATMENT_RECOVERY_COPY.providerStarted;
    default:
      return TREATMENT_RECOVERY_COPY.default;
  }
};

const displayStatus = (value) =>
  String(value || "pending").replace(/_/g, " ");

export default function TreatmentCheckoutRecovery({
  recovery,
  treatments = [],
  busy = false,
  message,
  quoteChange,
  onConfirmQuote,
  onAction,
}) {
  const [couponCode, setCouponCode] = useState("");
  if (!recovery && !quoteChange) return null;
  const actions = Array.isArray(recovery?.recovery_actions)
    ? recovery.recovery_actions
    : [];
  const stateCopy = recoveryCopy(recovery);
  const optionalProducts = treatments.flatMap((treatment) =>
    (treatment.products || [])
      .filter((product) => product.product_role === "optional_addon")
      .map((product) => ({
        ...product,
        treatmentName: treatmentName(treatment),
      }))
  );

  return (
    <section
      className="treatment-checkout-recovery"
      aria-labelledby="treatment-recovery-title"
    >
      <div className="treatment-checkout-recovery__heading">
        <span>{quoteChange ? "Quote updated" : stateCopy.eyebrow}</span>
        <h2 id="treatment-recovery-title">
          {quoteChange
            ? "Review your new total"
            : stateCopy.title}
        </h2>
        <p>
          {quoteChange
            ? "Your selection changed. Confirm this server-priced quote before resubmitting payment."
            : stateCopy.description}
        </p>
      </div>

      {message && (
        <div className="treatment-checkout-recovery__message" role="status">
          {message}
        </div>
      )}

      {!quoteChange && recovery && (
        <dl className="treatment-checkout-recovery__facts">
          <div>
            <dt>{TREATMENT_RECOVERY_COPY.recoveryStatus}</dt>
            <dd>{displayStatus(recovery.status)}</dd>
          </div>
          <div>
            <dt>
              {recovery.support_reference
                ? TREATMENT_RECOVERY_COPY.supportReference
                : TREATMENT_RECOVERY_COPY.attemptReference}
            </dt>
            <dd>
              {recovery.support_reference ||
                String(recovery.attempt_id || "").slice(0, 12) ||
                "Pending"}
            </dd>
          </div>
          {recovery.support_status && (
            <div>
              <dt>Support case</dt>
              <dd>{displayStatus(recovery.support_status)}</dd>
            </div>
          )}
        </dl>
      )}

      {quoteChange && (
        <div className="treatment-checkout-recovery__quote">
          <div>
            <span>Previous total</span>
            <strong>${Number(quoteChange.previous_total || 0).toFixed(2)}</strong>
          </div>
          <div>
            <span>Updated total</span>
            <strong>${Number(quoteChange.proposed_total || 0).toFixed(2)}</strong>
          </div>
          <button type="button" disabled={busy} onClick={onConfirmQuote}>
            Confirm updated quote
          </button>
        </div>
      )}

      {!quoteChange && actions.length > 0 && (
        <div className="treatment-checkout-recovery__actions">
        {actions
          .filter((action) => ![
            TREATMENT_RECOVERY_ACTION.removeTreatment,
            TREATMENT_RECOVERY_ACTION.reviewCoupon,
          ].includes(action))
          .map((action) => (
            <button
              key={action}
              type="button"
              disabled={busy}
              onClick={() => onAction(action)}
            >
              {TREATMENT_RECOVERY_ACTION_LABEL[action] || "Continue recovery"}
            </button>
          ))}
        </div>
      )}

      {!quoteChange && actions.length === 0 && (
        <div className="treatment-checkout-recovery__locked" role="status">
          This checkout is preserved and cannot be submitted again while its
          current state is being reconciled.
        </div>
      )}

      {!quoteChange &&
        actions.includes(TREATMENT_RECOVERY_ACTION.reviewCoupon) && (
          <div className="treatment-checkout-recovery__coupon">
            <label htmlFor="treatment-recovery-coupon">
              Coupon code
            </label>
            <div>
              <input
                id="treatment-recovery-coupon"
                value={couponCode}
                disabled={busy}
                autoComplete="off"
                onChange={(event) => setCouponCode(event.target.value)}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => onAction(
                  TREATMENT_RECOVERY_ACTION.reviewCoupon,
                  null,
                  null,
                  couponCode.trim(),
                )}
              >
                Reprice quote
              </button>
            </div>
          </div>
        )}

      {!quoteChange &&
        actions.includes(TREATMENT_RECOVERY_ACTION.removeOptional) &&
        optionalProducts.length > 0 && (
          <div className="treatment-checkout-recovery__treatments">
            <h3>Remove an optional item</h3>
            {optionalProducts.map((product) => (
              <button
                key={String(product.option_id)}
                type="button"
                disabled={busy}
                onClick={() => onAction(
                  TREATMENT_RECOVERY_ACTION.removeOptional,
                  null,
                  product.option_id,
                )}
              >
                Remove {product.name} from {product.treatmentName}
              </button>
            ))}
          </div>
        )}

      {!quoteChange &&
        actions.includes(TREATMENT_RECOVERY_ACTION.removeTreatment) && (
        <div className="treatment-checkout-recovery__treatments">
          <h3>Remove a treatment</h3>
          <p>
            Removing a treatment creates a new server-priced quote before
            payment can continue.
          </p>
          {treatments.map((treatment) => (
            <button
              key={String(treatment.treatment_type_id)}
              type="button"
              disabled={busy}
              onClick={() => onAction(
                TREATMENT_RECOVERY_ACTION.removeTreatment,
                treatment.treatment_type_id,
              )}
            >
              Remove {treatmentName(treatment)}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
